import React, { useEffect, useState, useRef } from 'react';
import { PaymentApi } from '../../../api/PaymentApi';
import { CardPayment, PaymentSuccessResponse } from '../CommonTypes';
import LoadingContainer from '../LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { CountryIsoCodes } from '../Constants';

type WorldPayProps = {
  token: string;
  isCallCentreUser: boolean;
  payment: CardPayment;
  registeringPolicy: boolean;
  voidTransaction: boolean;
  captureTransaction: boolean;
  policyNumbers: string;
  onPaymentSuccess: (
    transactionId: string,
    bankAuthorisationCode: string
  ) => void;
  onPaymentVoided: () => void;
  onPaymentCaptured: () => void;
  countryCode: string;
};

type Gateway = {
  paymentAmount?: string;
  paymentCurrency?: string;
  mac?: string;
  paymentStatus?: string;
  orderKey?: string;
};

type Error = {
  referenceNumber: string;
};

type Order = {
  orderKey: string;
  status: string;
  error: Error;
  gateway: Gateway;
};

type ResultCallbackData = {
  order: Order;
};

type FlowCallbackData = {
  page: string;
};

type CustomOptions = {
  url: string;
  type: string;
  inject?: string;
  target?: string;
  accessibility: boolean;
  debug: boolean;
  language: string;
  country: string;
  flowCallback: (data: FlowCallbackData) => void;
  resultCallback: (data: ResultCallbackData) => void;
};

type CardInfo = {
  cardNumber: string;
  cardHolderName: string;
  cardExpiryMonth: number;
  cardExpiryYear: number;
  cardSecurityCode: string;
};

type libraryObject = {
  setup: (customOptions: CustomOptions) => void;
  populate: (cardInfo: CardInfo) => void;
};

const WorldPay = ({
  token,
  isCallCentreUser,
  payment,
  registeringPolicy,
  voidTransaction,
  captureTransaction,
  policyNumbers,
  onPaymentSuccess,
  onPaymentVoided,
  onPaymentCaptured,
  countryCode
}: WorldPayProps): JSX.Element => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [optionsSet, setOptionsSet] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const { t } = useTranslation();
  const libraryRef = useRef<libraryObject>();

  useEffect(() => {
    const loadError = () => {
      setScriptError(true);
      console.log('WorldPay script failed to load');
    };

    const scriptUpdate = () => {
      setScriptLoaded(true);
      console.log('WorldPay script loaded');
    };

    const LoadExternalScript = () => {
      const externalScript = document.createElement('script');
      externalScript.id = 'worldPay';
      externalScript.onerror = loadError;
      externalScript.async = true;
      externalScript.type = 'text/javascript';
      externalScript.setAttribute('crossorigin', 'anonymous');
      externalScript.onload = scriptUpdate;
      document.head.appendChild(externalScript);
      externalScript.src =
        'https://payments.worldpay.com/resources/hpp/integrations/embedded/js/hpp-embedded-integration-library.js';
    };

    LoadExternalScript();
  }, []);

  useEffect(() => {
    if (scriptLoaded === false) {
      return;
    }

    const controller = new AbortController();

    const callback = (data: PaymentSuccessResponse) => {
      console.log(`Sending order success: ${JSON.stringify(data.acsUrl)}`);
      if (data.acsUrl) {
        setPaymentUrl(data.acsUrl);
        setOrderNo(data.transactionId);
      } else {
        setOptionsSet(true);
        setScriptError(true);
      }
    };

    const failureCallback = () => {
      console.log('Sending order failed');
    };

    const api = new PaymentApi();
    api.sendOrder(
      token,
      payment,
      isCallCentreUser,
      callback,
      failureCallback,
      countryCode,
      getLanguage(),
      controller.signal
    );

    return () => {
      controller.abort();
    };
  }, [scriptLoaded]);

  useEffect(() => {
    if (paymentUrl !== '' && optionsSet === false) {
      const customOptions: CustomOptions = {
        url: paymentUrl,
        type: 'iframe',
        target: 'custom-html',
        inject: 'immediate',
        accessibility: true,
        debug: true,
        language: getLanguage(),
        country: countryCode,
        flowCallback: flowCallback,
        resultCallback: resultCallback
      };

      //@ts-ignore
      libraryRef.current = new WPCL.Library();
      libraryRef.current?.setup(customOptions);

      console.log('WorldPay setup done');

      setOptionsSet(true);
    }
  }, [paymentUrl]);

  useEffect(() => {
    const controller = new AbortController();

    if (voidTransaction === true) {
      const callback = () => {
        onPaymentVoided();
      };

      const failureCallback = () => {
        onPaymentVoided();
      };

      new PaymentApi().updateWorldPayPayment(
        token,
        orderNo,
        'void',
        isCallCentreUser,
        payment.amount,
        policyNumbers,
        payment.firstName + ' ' + payment.surname,
        callback,
        failureCallback,
        countryCode,
        controller.signal
      );
    }

    return () => {
      controller.abort();
    };
  }, [voidTransaction]);

  useEffect(() => {
    const controller = new AbortController();

    if (captureTransaction === true) {
      const callback = () => {
        onPaymentCaptured();
      };

      const failureCallback = () => {
        onPaymentCaptured();
      };

      new PaymentApi().updateWorldPayPayment(
        token,
        orderNo,
        'capture',
        isCallCentreUser,
        payment.amount,
        policyNumbers,
        payment.firstName + ' ' + payment.surname,
        callback,
        failureCallback,
        countryCode,
        controller.signal
      );
    }

    return () => {
      controller.abort();
    };
  }, [captureTransaction]);

  const getLanguage = () => {
    if (countryCode === CountryIsoCodes.AT) {
      return CountryIsoCodes.DE;
    } else {
      return countryCode;
    }
  };

  const flowCallback = (result: FlowCallbackData) => {
    console.log(`flowCallback: ${JSON.stringify(result)}`);
  };

  const resultCallback = (responseData: ResultCallbackData) => {
    const status = responseData.order.status;

    submitPaymentResponse(
      status,
      responseData.order.error?.referenceNumber ?? ''
    );

    if (status === 'success') {
      onPaymentSuccess(orderNo, orderNo);
    }
  };

  const submitPaymentResponse = (status: string, error: string) => {
    const controller = new AbortController();

    const api = new PaymentApi();
    api.paymentResponse(token, orderNo, status, error, controller.signal);

    return () => {
      controller.abort();
    };
  };

  if (scriptError === true) {
    return <>ERROR</>;
  }

  return (
    <>
      <LoadingContainer
        loading={!optionsSet}
        message={t('customer-single-payment.loading')}></LoadingContainer>
      <LoadingContainer
        loading={registeringPolicy}
        message={t(
          'customer-single-payment.register-policy'
        )}></LoadingContainer>
      <div id='custom-html'></div>
    </>
  );
};

export default WorldPay;
